/**
 * Doserne Academy — Home (clean, focused).
 * Displays today's daily plan + "What's new" section. Quick-actions moved to burger menu.
 */
import React, { useEffect, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Loader2, Calendar, ArrowRight, ClipboardCheck, MapPin, Users, Sparkles,
  FolderKanban, Camera, FileText, Phone, Check,
} from "lucide-react";
import { useDoserneAuth } from "./doserneAuth";
import { useLang } from "./i18n";
import { useJarvisStatus } from "./jarvisStatus";
import JarvisHud from "./JarvisHud";
import DoserneShell from "./DoserneShell";
import RichContent from "./RichContent";
import { usePlanLang, FlagBar, translateContent, FLAG_LABEL } from "./planTranslate";
import MaterialListModal from "./MaterialListModal";
import ProgressFeed from "./ProgressFeed";
import NewCourseBanner from "./NewCourseBanner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function todayISO() { return new Date().toISOString().slice(0, 10); }

function fmtDate(iso, locale) {
  if (!iso) return "";
  try {
    const loc = locale === "ro" ? "ro-RO" : locale === "en" ? "en-GB" : "no-NO";
    return new Date(iso + "T00:00:00").toLocaleDateString(loc, {
      weekday: "long", day: "numeric", month: "long",
    });
  } catch { return iso; }
}

/* Announcements about new features — hard-coded for now.
   date must be YYYY-MM-DD. Newer entries first. */
const NEWS = [
  {
    date: "2026-07-18",
    emoji: "📚",
    tkey_title: { no: "Kurs er live", ro: "Cursurile sunt live", en: "Courses are live" },
    tkey_body: {
      no: "3 nye kurs tilgjengelig: Welcome to Doserne, Company Rules, Your First Day. Bestå for sertifikat.",
      ro: "3 cursuri noi disponibile: Welcome to Doserne, Company Rules, Your First Day. Promovează pentru certificat.",
      en: "3 new courses available: Welcome to Doserne, Company Rules, Your First Day. Pass for a certificate.",
    },
    link: "/doserne/kurs",
  },
  {
    date: "2026-07-18",
    emoji: "🌍",
    tkey_title: { no: "Flerspråklig", ro: "Multilingv", en: "Multilingual" },
    tkey_body: {
      no: "Doserne Academy snakker nå Norsk, Română og English. Endre språk fra menyen.",
      ro: "Doserne Academy vorbește acum Norvegiană, Română și Engleză. Schimbă limba din meniu.",
      en: "Doserne Academy now speaks Norwegian, Romanian and English. Change language from the menu.",
    },
    link: "/doserne/profil",
  },
  {
    date: "2026-07-18",
    emoji: "📸",
    tkey_title: { no: "Daglig progresjon", ro: "Progres zilnic", en: "Daily progress" },
    tkey_body: {
      no: "Legg til bilder fra jobben og sett dem på prosjektet — kan også backdatere.",
      ro: "Adaugă poze de pe șantier și le pui pe proiect — poți antedata până la 30 zile.",
      en: "Add photos from the job and pin them to the project — you can also backdate up to 30 days.",
    },
    link: "/doserne/progresjon",
  },
];

// Kurs/Certificates modules are behind a global "Coming Soon" screen.
// Hide any news items that link to them until the module goes live.
const VISIBLE_NEWS = NEWS.filter((n) => !n.link || !n.link.startsWith("/doserne/kurs"));

function BrandHero({ first, t, lang }) {
  const dateStr = new Date().toLocaleDateString(
    lang === "ro" ? "ro-RO" : lang === "en" ? "en-GB" : "no-NO",
    { weekday: "long", day: "numeric", month: "long" }
  );
  return (
    <section
      className="relative overflow-hidden rounded-3xl border border-emerald-200 bg-gradient-to-br from-emerald-600 via-emerald-500 to-orange-500 shadow-lg shadow-emerald-500/10 mb-8"
      data-testid="doserne-hero"
    >
      {/* Decorative rings */}
      <div className="pointer-events-none absolute -top-24 -right-24 w-72 h-72 rounded-full bg-white/10 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-20 -left-20 w-60 h-60 rounded-full bg-orange-300/20 blur-3xl" />
      <div className="pointer-events-none absolute inset-0 opacity-20"
        style={{ backgroundImage: "radial-gradient(circle at 1px 1px, rgba(255,255,255,0.4) 1px, transparent 0)", backgroundSize: "24px 24px" }} />

      <div className="relative px-4 sm:px-8 md:px-10 py-6 sm:py-8 md:py-10 flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-6">
        <div className="flex-shrink-0 w-16 h-16 sm:w-20 sm:h-20 md:w-24 md:h-24 rounded-2xl bg-white shadow-xl flex items-center justify-center p-2.5 sm:p-3">
          <img
            src="/allstillas-logo.png"
            alt="Allstillas"
            className="w-full h-full object-contain"
          />
        </div>
        <div className="flex-1 min-w-0" style={{ color: "#fff" }}>
          <div className="inline-flex items-center gap-1.5 rounded-full bg-white/20 backdrop-blur px-2.5 py-0.5 mb-2">
            <Sparkles className="w-3 h-3" />
            <span className="text-[10px] uppercase tracking-widest font-black">Doserne Academy</span>
          </div>
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-black tracking-tight leading-tight break-words">
            {t("greet.hi")}, {first} <span className="inline-block">👋</span>
          </h1>
          <p className="text-xs sm:text-sm md:text-base opacity-90 mt-1 capitalize">{dateStr}</p>
        </div>
      </div>
    </section>
  );
}

function TodayPlanCard({ plan, isToday, t, lang, authHeader, projectAddresses, planLang = "no", onPlanLang, onOpenMaterialList, onOpenProject }) {
  const [tBody, setTBody] = useState(null);
  const [tBusy, setTBusy] = useState(false);
  const displayBody = planLang && tBody ? tBody : plan.body;
  const bodyIsHtml = displayBody && /<\/?[a-z][\s\S]*>/i.test(displayBody);
  const cardBg = plan.background_color || "";

  useEffect(() => {
    let alive = true;
    if (!planLang || !plan.body || !plan.body.trim()) { setTBody(null); return; }
    setTBusy(true);
    translateContent(plan.body, planLang, authHeader)
      .then((txt) => { if (alive) setTBody(txt); })
      .finally(() => { if (alive) setTBusy(false); });
    return () => { alive = false; };
  }, [planLang, plan.body, authHeader]);

  return (
    <div
      className="rounded-2xl border-2 border-slate-200 overflow-hidden"
      style={{ backgroundColor: cardBg || "#ffffff" }}
      data-testid="today-plan-card"
    >
      <div className="px-5 sm:px-6 py-4 border-b border-slate-100 flex items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">
            {isToday ? t("home.today") : t("home.latest_plan")} · {fmtDate(plan.plan_date, lang)}
          </div>
          <h2 className="text-xl sm:text-2xl font-black text-slate-900 truncate">{plan.title}</h2>
        </div>
        {onPlanLang && <FlagBar value={planLang} onChange={onPlanLang} className="flex-shrink-0" />}
      </div>
      {displayBody && (
        <div className="px-5 sm:px-6 py-4 text-sm text-slate-700 leading-relaxed">
          {planLang && (tBusy || tBody) && (
            <div className="inline-flex items-center gap-1.5 mb-2 px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 text-[10px] font-bold uppercase tracking-wide">
              {tBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
              {FLAG_LABEL[planLang] || planLang}
            </div>
          )}
          {bodyIsHtml
            ? <RichContent html={displayBody} onOpenMaterialList={onOpenMaterialList} onOpenProject={onOpenProject} projectAddresses={projectAddresses} />
            : <div className="whitespace-pre-wrap">{displayBody}</div>}
        </div>
      )}
      {plan.teams?.length > 0 && (
        <div className="px-5 sm:px-6 pb-5 space-y-2">
          {plan.teams.map((tm, i) => (
            <div key={i} className="flex items-start gap-3 border border-slate-200 rounded-xl px-3 py-2.5 bg-white/70">
              <div className="w-8 h-8 rounded-lg bg-slate-900 flex items-center justify-center flex-shrink-0" style={{ color: "#fff" }}>
                <ClipboardCheck className="w-4 h-4" />
              </div>
              <div className="flex-1 min-w-0 text-sm">
                <div className="font-bold text-slate-900">{tm.name || "Team"}</div>
                {tm.location && <div className="text-slate-600 text-xs flex items-center gap-1 mt-0.5"><MapPin className="w-3 h-3" /> {tm.location}</div>}
                {tm.workers?.length > 0 && <div className="text-slate-600 text-xs flex items-center gap-1 mt-0.5"><Users className="w-3 h-3" /> {tm.workers.join(", ")}</div>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function MissingPhoneBanner({ authHeader }) {
  // Shows a persistent yellow banner while the user has no phone on file.
  // Inline mini-form lets them save it with one tap — no navigation required.
  const { t } = useLang();
  const [phone, setPhone] = useState("");
  const [known, setKnown] = useState(null); // null=loading, ""=missing, "..." = present
  const [saving, setSaving] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/doserne/prefs`, authHeader());
        if (alive) setKnown(data.phone || "");
      } catch {
        if (alive) setKnown("skip"); // Don't nag on API errors — silent fail
      }
    })();
    return () => { alive = false; };
  }, [authHeader]);

  const save = async (e) => {
    e.preventDefault();
    const p = phone.trim();
    if (p.length < 4) return;
    setSaving(true);
    try {
      await axios.put(`${API}/doserne/prefs`, { phone: p }, authHeader());
      setKnown(p);
      setSavedFlash(true);
      // Auto-hide after a second so the "Tusen takk" glow reads naturally.
      setTimeout(() => setSavedFlash(false), 1600);
    } catch {
      // Silent — user can retry
    } finally {
      setSaving(false);
    }
  };

  if (known === null || known === "skip") return null;
  if (known && !savedFlash) return null; // Phone present → banner gone

  if (savedFlash) {
    return (
      <div className="mb-4 flex items-center gap-3 bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-2xl px-4 py-3 shadow-sm"
           data-testid="phone-saved-flash">
        <div className="w-9 h-9 rounded-full bg-emerald-500 flex items-center justify-center flex-shrink-0">
          <Check className="w-5 h-5" style={{ color: "#fff" }} />
        </div>
        <div className="text-sm font-semibold">{t("banner.phone.saved", { phone: known })}</div>
      </div>
    );
  }

  return (
    <div className="mb-4 bg-gradient-to-r from-amber-50 to-orange-50 border-2 border-amber-300 rounded-2xl px-4 py-4 shadow-sm"
         data-testid="missing-phone-banner">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-full bg-amber-500 flex items-center justify-center flex-shrink-0">
          <Phone className="w-5 h-5" style={{ color: "#fff" }} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-black text-amber-900">{t("banner.phone.title")}</div>
          <div className="text-xs text-amber-800 mt-0.5 leading-relaxed">
            {t("banner.phone.subtitle")}
          </div>
          <form onSubmit={save} className="mt-3 flex flex-col sm:flex-row gap-2">
            <input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder={t("banner.phone.placeholder")}
              autoFocus
              className="flex-1 px-3 py-2 border border-amber-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-amber-500"
              data-testid="missing-phone-input"
            />
            <button
              type="submit"
              disabled={saving || phone.trim().length < 4}
              className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-amber-600 hover:bg-amber-700 disabled:opacity-50 text-sm font-black uppercase tracking-widest shadow"
              style={{ color: "#fff" }}
              data-testid="missing-phone-save"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
              {t("banner.phone.save")}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

function Dashboard() {
  const { user, authHeader } = useDoserneAuth();
  const { t, lang } = useLang();
  const { active, setActive } = useJarvisStatus();
  const navigate = useNavigate();
  const [plan, setPlan] = useState(null);
  const [projectAddresses, setProjectAddresses] = useState({});
  const [planLang, setPlanLang] = usePlanLang(authHeader);
  const [loading, setLoading] = useState(true);
  const [materialListId, setMaterialListId] = useState(null);

  const openMaterialList = (listId) => setMaterialListId(listId);
  const closeMaterialList = () => setMaterialListId(null);
  const openProject = (projectId) => navigate(`/doserne/prosjekter/${projectId}?from_plan=today`);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/doserne/daily-plans?limit=1`, authHeader());
        setPlan(data.plans?.[0] || null);
        setProjectAddresses(data.project_addresses || {});
      } catch { /* silent */ }
      setLoading(false);
    })();
  }, []);

  const today = todayISO();
  const isToday = plan && plan.plan_date === today;
  const first = user?.first_name || (user?.full_name || "").split(" ")[0] || t("greet.hi");

  const quickActions = [
    { to: "/doserne/plan",       icon: Calendar,    label: t("nav.daily_plan"), tone: "emerald" },
    { to: "/doserne/prosjekter", icon: FolderKanban, label: t("nav.projects"),  tone: "orange" },
    { to: "/doserne/progresjon", icon: Camera,       label: t("nav.progress"),  tone: "emerald" },
    { to: "/underlag",           icon: FileText,     label: t("nav.underlag"),  tone: "orange",  external: true },
  ];

  const TONE = {
    emerald: {
      wrap: "border-emerald-200 hover:border-emerald-400 hover:shadow-emerald-100",
      icon: "bg-emerald-100 text-emerald-700",
    },
    orange: {
      wrap: "border-orange-200 hover:border-orange-400 hover:shadow-orange-100",
      icon: "bg-orange-100 text-orange-700",
    },
  };

  return (
    <DoserneShell>
      {user?.is_admin && active && (
        <JarvisHud authHeader={authHeader} onExit={() => setActive(false)} />
      )}
      {/* HERO — branded welcome with logo. Hero scrolls away with the
          rest of the page; only the shell's top bar stays pinned (via
          `position: sticky` on the header). Stacking two stickies here
          was leaking through the header and confusing users. */}
      <div className="-mx-4 sm:-mx-0 mb-6 sm:mb-8" data-testid="home-hero">
        <BrandHero first={first} t={t} lang={lang} />
      </div>

      {/* Missing-phone banner — sticky nudge for first-time users. Stays on
          the home page until the user fills in a phone number so admins &
          colleagues can call/SMS them. Never dismissable — required data. */}
      <MissingPhoneBanner authHeader={authHeader} />

      {/* New-course announcement — auto-hides once the user passes the
          first HSE course. Shown to every worker until then. */}
      <NewCourseBanner authHeader={authHeader} />

      {/* QUICK ACTIONS */}
      <section className="mb-8">
        <div className="text-xs uppercase tracking-widest text-slate-500 font-bold mb-3">{t("greet.what_now")}</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {quickActions.map((a) => {
            const Icon = a.icon;
            const tone = TONE[a.tone];
            const inner = (
              <div className={`h-full bg-white border-2 rounded-2xl p-4 flex flex-col gap-2 transition hover:-translate-y-0.5 ${tone.wrap}`}>
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${tone.icon}`}>
                  <Icon className="w-5 h-5" strokeWidth={2} />
                </div>
                <div className="text-sm font-black text-slate-900 leading-tight">{a.label}</div>
                <ArrowRight className="w-4 h-4 text-slate-300 mt-auto" />
              </div>
            );
            return a.external ? (
              <a key={a.to} href={a.to} className="block" data-testid={`quick-${a.to}`}>{inner}</a>
            ) : (
              <Link key={a.to} to={a.to} className="block" data-testid={`quick-${a.to}`}>{inner}</Link>
            );
          })}
        </div>
      </section>

      {/* PRIMARY — today's plan */}
      <section className="mb-8">
        <div className="flex items-center justify-between mb-3">
          <div className="text-xs uppercase tracking-widest text-slate-500 font-bold">{t("greet.today_focus")}</div>
          <Link
            to="/doserne/plan"
            className="text-xs text-emerald-700 hover:text-emerald-900 font-semibold inline-flex items-center gap-1"
            data-testid="home-plan-see-all"
          >
            {t("home.see_all_plans")} <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>
        {loading ? (
          <div className="bg-white border border-slate-200 rounded-2xl p-8 flex justify-center">
            <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
          </div>
        ) : plan ? (
          <TodayPlanCard
            plan={plan}
            isToday={isToday}
            t={t}
            lang={lang}
            authHeader={authHeader}
            projectAddresses={projectAddresses}
            planLang={planLang}
            onPlanLang={setPlanLang}
            onOpenMaterialList={openMaterialList}
            onOpenProject={openProject}
          />
        ) : (
          <div className="bg-white border-2 border-dashed border-emerald-200 rounded-2xl p-8 text-center">
            <Calendar className="w-8 h-8 text-emerald-300 mx-auto mb-2" />
            <div className="text-sm text-slate-500 mb-3">{t("home.no_plan_yet")}</div>
            <Link to="/doserne/plan" className="text-xs text-emerald-700 hover:text-emerald-900 font-semibold">
              {t("home.see_all_plans")} →
            </Link>
          </div>
        )}
      </section>

      {/* PROGRESS FEED — Facebook-style timeline of latest job photos */}
      <ProgressFeed authHeader={authHeader} t={t} lang={lang} />

      {/* WHAT'S NEW */}
      <section>
        <div className="text-xs uppercase tracking-widest text-slate-500 font-bold mb-3 inline-flex items-center gap-1.5">
          <Sparkles className="w-3.5 h-3.5 text-orange-500" /> {t("home.whats_new")}
        </div>
        {VISIBLE_NEWS.length === 0 ? (
          <div className="bg-white border border-slate-200 rounded-2xl p-6 text-center text-sm text-slate-500">{t("home.no_news")}</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {VISIBLE_NEWS.map((n, i) => {
              const inner = (
                <div className="h-full bg-white border border-slate-200 rounded-2xl p-4 hover:border-emerald-400 hover:shadow-md transition">
                  <div className="text-3xl mb-2">{n.emoji}</div>
                  <div className="text-sm font-bold text-slate-900 leading-tight mb-1">{n.tkey_title[lang] || n.tkey_title.no}</div>
                  <div className="text-xs text-slate-500 leading-relaxed">{n.tkey_body[lang] || n.tkey_body.no}</div>
                  <div className="mt-3 flex items-center justify-between">
                    <span className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">{fmtDate(n.date, lang)}</span>
                    <ArrowRight className="w-4 h-4 text-slate-300 group-hover:text-emerald-600" />
                  </div>
                </div>
              );
              return n.link ? <Link key={i} to={n.link} className="group" data-testid={`news-${i}`}>{inner}</Link> : <div key={i}>{inner}</div>;
            })}
          </div>
        )}
      </section>
      {materialListId && (
        <MaterialListModal
          listId={materialListId}
          onClose={closeMaterialList}
          authHeader={authHeader}
          canDelete={false}
        />
      )}
    </DoserneShell>
  );
}

export default function DoserneHome() {
  const { user, loading } = useDoserneAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center bg-[#fafaf7]"><Loader2 className="w-8 h-8 animate-spin text-slate-400" /></div>;
  if (!user) return <Navigate to="/doserne/logg-inn" replace />;
  return <Dashboard />;
}
